import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  BookOpen, 
  ArrowLeft,
  Search,
  FileText,
  ChevronRight,
  BookMarked,
  Layers
} from 'lucide-react';

const RPATPlaybooks = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [playbooks, setPlaybooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [error, setError] = useState(null);

  const fetchPlaybooks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/playbooks');
      setPlaybooks(res.data.playbooks || []);
    } catch (err) {
      console.error('Failed to fetch playbooks:', err);
      setError(err.response?.data?.detail || 'Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  // Get unique phases
  const phases = [...new Set(playbooks.filter(p => p.phase_name).map(p => p.phase_name))];

  // Filter playbooks
  const filteredPlaybooks = playbooks.filter(playbook => {
    const matchesSearch = !searchQuery || 
      playbook.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      playbook.topic_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      playbook.topic_code?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPhase = selectedPhase === 'all' || playbook.phase_name === selectedPhase;
    
    return matchesSearch && matchesPhase;
  });

  // Group by phase for display
  const groupedByPhase = filteredPlaybooks.reduce((acc, playbook) => {
    const phase = playbook.phase_name || 'General';
    if (!acc[phase]) acc[phase] = [];
    acc[phase].push(playbook);
    return acc;
  }, {});

  const getPhaseColor = (phaseName) => {
    const colors = {
      'Foundation': 'from-cyan-500 to-blue-600',
      'Core Skills': 'from-violet-500 to-purple-600',
      'Communication & Interview': 'from-amber-500 to-orange-600',
      'Practical Application': 'from-emerald-500 to-green-600'
    };
    return colors[phaseName] || 'from-slate-500 to-gray-600';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-full max-w-md" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load Playbooks</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate('/rpat')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to R-PAT
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')} data-testid="back-to-rpat">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              R-PAT Playbooks
            </h1>
            <p className="text-muted-foreground">
              1,200+ pages of comprehensive reference materials
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Playbooks</p>
                  <p className="text-2xl font-bold text-violet-700">{playbooks.length}</p>
                </div>
                <BookMarked className="h-8 w-8 text-violet-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Pages</p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {playbooks.reduce((sum, p) => sum + (p.total_pages || 0), 0).toLocaleString()}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-cyan-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Chapters</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {playbooks.reduce((sum, p) => sum + (p.chapters_count || 0), 0)}
                  </p>
                </div>
                <Layers className="h-8 w-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Phases Covered</p>
                  <p className="text-2xl font-bold text-emerald-700">{phases.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search playbooks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="playbook-search"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedPhase === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPhase('all')}
              data-testid="filter-all"
            >
              All
            </Button>
            {phases.map(phase => (
              <Button
                key={phase}
                variant={selectedPhase === phase ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPhase(phase)}
                data-testid={`filter-${phase.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {phase}
              </Button>
            ))}
          </div>
        </div>

        {/* Playbooks Grid */}
        {filteredPlaybooks.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No playbooks match your search' : 'No playbooks available yet'}
            </p>
          </Card>
        ) : (
          Object.entries(groupedByPhase).map(([phaseName, phasePlaybooks]) => (
            <div key={phaseName} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`w-1 h-6 rounded-full bg-gradient-to-b ${getPhaseColor(phaseName)}`} />
                <h2 className="text-lg font-semibold">{phaseName}</h2>
                <Badge variant="secondary" className="text-xs">
                  {phasePlaybooks.length} playbook{phasePlaybooks.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {phasePlaybooks.map(playbook => (
                  <Card 
                    key={playbook.id}
                    className="hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => navigate(`/rpat/playbook/${playbook.id}`)}
                    data-testid={`playbook-card-${playbook.topic_code || playbook.id}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${getPhaseColor(phaseName)} text-white`}>
                          <BookOpen className="h-5 w-5" />
                        </div>
                        {playbook.topic_code && (
                          <Badge variant="outline" className="text-xs">
                            {playbook.topic_code}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold mb-1 line-clamp-2 group-hover:text-violet-600 transition-colors">
                        {playbook.title}
                      </h3>
                      
                      {playbook.topic_title && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                          {playbook.topic_title}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {playbook.total_pages || 0} pages
                          </span>
                          <span className="flex items-center gap-1">
                            <Layers className="h-3 w-3" />
                            {playbook.chapters_count || 0} chapters
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </MainLayout>
  );
};

export default RPATPlaybooks;
